import { createAsyncAction, ActionType, createAction } from 'typesafe-actions';
import { FetchAction, SuccessFetchActionPayload, DefaultErrorPayload } from '../dataFetching/generators';
import { DataFetchingThunkAction } from '../types';
import { PriceFormatConfigOptions, PrimaryProductInfo } from '../products/types';
import { PDPApiResponse, PdpRequestQueryParam, ProductSimpleVideoResponse, TrackingVideoActions } from './types';
import { log404ErrorAsWarning } from 'Common/apiAdapters/ErrorHandlers';

export const changeProductDetailSizeAction = createAction('@PRODUCT_DETAILS/CHANGE_SIZE', action => {
    return ({
        simple,
        p100PdpShowMoodImageFirst,
        shopDeliveryTimePromise,
    }: {
        simple: string;
        p100PdpShowMoodImageFirst: boolean;
        shopDeliveryTimePromise: boolean;
    }) => action({ simple, p100PdpShowMoodImageFirst, shopDeliveryTimePromise });
});

export const setTrackingFlag = createAction('@PRODUCT_DETAILS/SET_TRACKING_FLAG', action => {
    return (event: TrackingVideoActions, percent?: number) => action({ event, percent });
});

export const setIsBisrSubscribed = createAction('@PRODUCT_DETAILS/SET_IS_BISR_SUBSCRIBED');

export const setSelectedQuantity = createAction('@PRODUCT_DETAILS/SET_SELECTED_QTY', action => {
    return (selectedQuantity: number) => action({ selectedQuantity });
});

export const setFromPLPWithMood = createAction('@PRODUCT_DETAILS/SET_FROM_PLP_WITH_MOOD', action => {
    return (fromPlpWithMood: boolean) => action({ fromPlpWithMood });
});

export const setSizeSelectorIsOpen = createAction('@PRODUCT_DETAILS/SET_SIZE_SELECTOR_IS_OPEN', action => {
    return (sizeSelectorIsOpen: boolean) => action({ sizeSelectorIsOpen });
});

export const setPDPProductKnownInfoAction = createAction('@PRODUCT_DETAILS/SET_INFO', action => {
    return (product: PrimaryProductInfo, p100PdpShowMoodImageFirst: boolean) =>
        action({ product, p100PdpShowMoodImageFirst });
});

export interface FetchProductDetailsActions extends SuccessFetchActionPayload<PDPApiResponse> {
    shopDeliveryTimePromise: boolean;
}

export const fetchProductDetailsActions = createAsyncAction(
    '@PRODUCT_DETAILS/FETCH_START',
    '@PRODUCT_DETAILS/FETCH_SUCCESS',
    '@PRODUCT_DETAILS/FETCH_FAILED'
)<FetchAction, FetchProductDetailsActions, DefaultErrorPayload>();

interface ReducerPayloadOptions extends PriceFormatConfigOptions {
    p100PdpShowMoodImageFirst: boolean;
    shopDeliveryTimePromise: boolean;
}

export const fetchProductDetailsIfNeeded = ({
    urlKey,
    queryParams,
    reducerPayloadOptions,
}: {
    urlKey: string;
    queryParams: PdpRequestQueryParam;
    reducerPayloadOptions: ReducerPayloadOptions;
}): DataFetchingThunkAction => async (dispatch, getState, deps) => {
    const { apiService, logger } = deps;
    const query = queryParams.simpleSku ? `?simple=${queryParams.simpleSku}` : '';
    const identifier = `${urlKey}${query}`;
    if (identifier === getState().productdetails.identifier) {
        return;
    }
    dispatch(fetchProductDetailsActions.request({ identifier }));
    try {
        const productsDetails = await apiService.shopApi.getProductsForDetailPage(urlKey, queryParams);
        if (identifier !== getState().productdetails.identifier) {
            return;
        }
        dispatch(
            fetchProductDetailsActions.success({
                data: productsDetails,
                identifier,
                reducerPayloadOptions,
                shopDeliveryTimePromise: reducerPayloadOptions.shopDeliveryTimePromise,
            })
        );
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchProductsDetailsIfNeeded');
        dispatch(fetchProductDetailsActions.failure({ error: err, identifier }));
    }
};

export const fetchProductSimpleVideoActions = createAsyncAction(
    '@PRODUCT_DETAILS_SIMPLE_VIDEO/FETCH_START',
    '@PRODUCT_DETAILS_SIMPLE_VIDEO/FETCH_SUCCESS',
    '@PRODUCT_DETAILS_SIMPLE_VIDEO/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<ProductSimpleVideoResponse>, DefaultErrorPayload>();

export const fetchProductSimpleVideoIfNeeded = (urlKey: string, simpleSku: string): DataFetchingThunkAction => async (
    dispatch,
    getState,
    { apiService, logger }
) => {
    const identifier = simpleSku;
    if (!identifier) {
        return;
    }
    dispatch(fetchProductSimpleVideoActions.request({ identifier }));
    try {
        const video = await apiService.shopApi.getProductVideoBySimpleSku(urlKey, { simpleSku });

        dispatch(
            fetchProductSimpleVideoActions.success({
                data: video,
                identifier,
            })
        );
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchProductSimpleVideoIfNeeded');
        dispatch(fetchProductSimpleVideoActions.failure({ error: err, identifier }));
    }
};

export type ProductDetailsActionsType =
    | ActionType<typeof fetchProductDetailsActions>
    | ActionType<typeof fetchProductSimpleVideoActions>
    | ActionType<typeof changeProductDetailSizeAction>
    | ActionType<typeof setPDPProductKnownInfoAction>
    | ActionType<typeof setTrackingFlag>
    | ActionType<typeof setSelectedQuantity>
    | ActionType<typeof setIsBisrSubscribed>
    | ActionType<typeof setSizeSelectorIsOpen>
    | ActionType<typeof setFromPLPWithMood>;
